import { Button } from '@components/_ui/Button'
import { Input } from '@components/_ui/Input'
import { Loader } from '@components/_ui/Loader'
import { useFetch } from '@hooks/useFetch'
import { usePriceSetter } from '@hooks/usePriceSetter'
import { useSteps } from '@hooks/useSteps'
import { transformFormData } from '@lib/transformFormData'
import { useLayoutEffect, useRef, useState } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { PatternFormat } from 'react-number-format'

import s from './Form.module.scss'
import type { FormProps } from './Form.props'

export function Form({ data }: FormProps) {
  const [disabled, setDisabled] = useState(false)

  const { register, setValue, formState: { errors } } = useFormContext()

  const phoneValue = useWatch({ name: 'phone' }) as string
  const nameValue = useWatch({ name: 'name' })
  const emailValue = useWatch({ name: 'email' })

  const resetHandler = () => {
    setValue('phone', '')
    setValue('name', '')
    setValue('email', '')
    setDisabled(false)
  }

  useLayoutEffect(() => {
    setDisabled(Boolean(phoneValue && nameValue && emailValue))
  }, [])

  return (
    <div className={s.wrapper}>
      <div className={s.form}>
        {data?.length && data.map((question) => (
          question.question_type === 'phone'
            ? (
              <PatternFormat
                key={question.db_field_name}
                format="+1 (###) ###-####"
                customInput={Input}
                onValueChange={(values) => setValue(question.db_field_name, values.value)}
                value={phoneValue}
                placeholder={question.placeholder}
                className={s.input}
                error={errors?.[question.db_field_name]?.message as string}
                valueIsNumericString
                disabled={disabled}
                getInputRef={register(
                  question.db_field_name,
                  { pattern: { value: /^[2-9][0-9]{9}$/, message: 'Invalid phone' } }
                ).ref}
              />
            )
            : (
              <Input
                key={question.db_field_name}
                {...register(
                  question.db_field_name,
                  {
                    name: { minLength: { value: 2, message: 'Minimum length 2' } },
                    email: { pattern: { value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/, message: 'Inavlid email' } }
                  }[question.db_field_name]
                )}
                autoComplete={question.db_field_name}
                type={question.question_type}
                placeholder={question.placeholder}
                className={s.input}
                error={errors?.[question.db_field_name]?.message as string}
                required
                disabled={disabled}
                onChange={(e) => setValue(
                  question.db_field_name,
                  question.db_field_name === 'name'
                    ? e.target.value.replace(/\b\w/g, char => char.toUpperCase())
                    : e.target.value
                )}
              />
            )
        ))}
      </div>

      <button
        className={s.editBtn}
        onClick={resetHandler}
      >
        Edit values
      </button>
    </div>
  )
}

function FormButton({ data }: FormProps) {
  const isFirstRender = useRef(true)

  const { handleSubmit, setError } = useFormContext()
  const { trigger, isLoading } = useFetch()
  const { stepsHistory, setStepsHistory, setCurrentStep } = useSteps()
  const setPriceData = usePriceSetter()

  const [skip, setSkip] = useState(false)

  const stepFields = data.map((question) => question.db_field_name)

  const values = useWatch({ name: stepFields })
  const cleaningType = useWatch({ name: 'requested_cleaning_type' })

  const disabled = values.some((value) => !value)

  const nextStep = cleaningType === 'airbnb' ? 7 : 5
  const newHistory = [...stepsHistory, nextStep]

  const goToNextStep = () => {
    setCurrentStep(nextStep)
    setStepsHistory(newHistory)
  }

  const nextHandler = handleSubmit(async (formData) => {
    if (disabled) return

    const transformedFormData = transformFormData(data, formData)

    trigger({ ...transformedFormData, current_screen: nextStep, history: newHistory })
      .then((res) => {
        if (res?.data?.success) {
          (window as any)?.fbq?.('track', 'Lead')

          setPriceData((prev) => ({ ...prev, ...res?.data?.data }))
          goToNextStep()
        } else {
          setError('email', { message: res?.data?.message })
        }
      })
  })

  useLayoutEffect(() => {
    setSkip(isFirstRender.current && values.every((value) => value))
    isFirstRender.current = false
  }, [values])

  return (
    <Button
      onClick={skip ? goToNextStep : nextHandler}
      disabled={isLoading || disabled}
    >
      {isLoading ? <Loader /> : 'Calculate My Quote'}
    </Button>
  )
}

Form.Button = FormButton
